import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import TabSection from '../components/TabSection';
import { SITE_URL } from '../config';
import FormViewer from '../components/FormViewer';
import form from '../assets/forms/pdfForms/Canada-Disability-Benefit.pdf';
import formFr from '../assets/forms/pdfForms/FR/Prestation-canadienne-pour-les-personnes-handicapées.pdf';
import page1 from '../assets/forms/jpgForms/Canada-Disability-Benefit/Page-1.jpg';
import page2 from '../assets/forms/jpgForms/Canada-Disability-Benefit/Page-2.jpg';
import page3 from '../assets/forms/jpgForms/Canada-Disability-Benefit/Page-3.jpg';
import page4 from '../assets/forms/jpgForms/Canada-Disability-Benefit/Page-4.jpg';
import page5 from '../assets/forms/jpgForms/Canada-Disability-Benefit/Page-5.jpg';
import page1Fr from '../assets/forms/jpgForms/Canada-Disability-Benefit/FR/page-1.jpg';
import page2Fr from '../assets/forms/jpgForms/Canada-Disability-Benefit/FR/page-2.jpg';
import page3Fr from '../assets/forms/jpgForms/Canada-Disability-Benefit/FR/page-3.jpg';
import page4Fr from '../assets/forms/jpgForms/Canada-Disability-Benefit/FR/page-4.jpg';
import page5Fr from '../assets/forms/jpgForms/Canada-Disability-Benefit/FR/page-5.jpg';

const enPages = [page1, page2, page3, page4, page5];
const frPages = [page1Fr, page2Fr, page3Fr, page4Fr, page5Fr];

const enOverlays = [
  {
    pageIndex: 0,
    sectionId: 'sections.beginning',
    top: '1%',
    height: '98%',
    videoUrls: ["/ASL/DISC ASL CDB Beginning.mp4", "/LSQ/PCPH form beginning.mp4"],
    videoLabels: ["ASL", "LSQ"],
  },
  {
    pageIndex: 1,
    sectionId: 'sections.beginning',
    top: '1%',
    height: '98%',
    videoUrls: ["/ASL/DISC ASL CDB Beginning.mp4", "/LSQ/PCPH form beginning.mp4"],
    videoLabels: ["ASL", "LSQ"],
  },
  {
    pageIndex: 2,
    sectionId: 'sections.cdb-section-a',
    top: '17%',
    height: '40.9%',
    videoUrls: ["/ASL/DISC ASL CDB Section A.mp4", "/LSQ/PCPH - Section A – Renseignements sur le demandeur.mp4"],
    videoLabels: ["ASL", "LSQ"],
  },
  {
    pageIndex: 2,
    sectionId: 'sections.cdb-section-b',
    top: '57.9%',
    height: '26%',
    videoUrls: ["/ASL/DISC ASL CDB Section B.mp4", "/LSQ/PCPH - Section B – Statut au Canada.mp4"],
    videoLabels: ["ASL", "LSQ"],
  },
  {
    pageIndex: 3,
    sectionId: 'sections.cdb-section-c',
    top: '8.5%',
    height: '68%',
    videoUrls: ["/ASL/DISC ASL CDB Section C.mp4", "/LSQ/PCPH -Section C – Réception des paiements.mp4"],
    videoLabels: ["ASL", "LSQ"],
  },
  {
    pageIndex: 4,
    sectionId: 'sections.cdb-section-d',
    top: '8.5%',
    height: '55%',
    videoUrls: ["/ASL/DISC ASL CDB Section D.mp4", "/LSQ/PCPH -Section D – Déclaration.mp4"],
    videoLabels: ["ASL", "LSQ"],
  },
];

// FR overlays start as a copy of EN. Only adjust top/height entries where the
// French form layout differs from the English one.
const frOverlays = [
  {
    pageIndex: 0,
    sectionId: 'sections.beginning',
    top: '1%',
    height: '98%',
    videoUrls: ["/ASL/DISC ASL CDB Beginning.mp4", "/LSQ/PCPH form beginning.mp4"],
    videoLabels: ["ASL", "LSQ"],
  },
  {
    pageIndex: 1,
    sectionId: 'sections.beginning',
    top: '1%',
    height: '98%',
    videoUrls: ["/ASL/DISC ASL CDB Beginning.mp4", "/LSQ/PCPH form beginning.mp4"],
    videoLabels: ["ASL", "LSQ"],
  },
  {
    pageIndex: 2,
    sectionId: 'sections.cdb-section-a',
    top: '17%',
    height: '40.9%',
    videoUrls: ["/ASL/DISC ASL CDB Section A.mp4", "/LSQ/PCPH - Section A – Renseignements sur le demandeur.mp4"],
    videoLabels: ["ASL", "LSQ"],
  },
  {
    pageIndex: 2,
    sectionId: 'sections.cdb-section-b',
    top: '57.9%',
    height: '26%',
    videoUrls: ["/ASL/DISC ASL CDB Section B.mp4", "/LSQ/PCPH - Section B – Statut au Canada.mp4"],
    videoLabels: ["ASL", "LSQ"],
  },
  {
    pageIndex: 3,
    sectionId: 'sections.cdb-section-c',
    top: '8.5%',
    height: '68%',
    videoUrls: ["/ASL/DISC ASL CDB Section C.mp4", "/LSQ/PCPH -Section C – Réception des paiements.mp4"],
    videoLabels: ["ASL", "LSQ"],
  },
  {
    pageIndex: 4,
    sectionId: 'sections.cdb-section-d',
    top: '8.5%',
    height: '59%',
    videoUrls: ["/ASL/DISC ASL CDB Section D.mp4", "/LSQ/PCPH -Section D – Déclaration.mp4"],
    videoLabels: ["ASL", "LSQ"],
  },
];

function Form2({ activeSectionId, setActiveSectionId, setCurrentVideoUrl, setVideoSidebarOpen, setVideoLabels, setSelectedVideoIndex, setAllVideoUrls, registerSection, unregisterSection }) {
  const { t, i18n } = useTranslation();
  const isFR = i18n.language === 'fr';
  const formImages = isFR ? frPages : enPages;
  const pageOverlays = isFR ? frOverlays : enOverlays;

  return (
    <>
      <Helmet>
        <title>{isFR ? 'Prestation canadienne pour les personnes handicapées | deafinfo.ca/fr | ASC-CAD' : 'Canada Disability Benefit | deafinfo.ca | CAD-ASC'}</title>
        <meta name="description" content="CAD-ASC provides resources and support for Canadians navigating disability benefits and tax credits." />
        <meta property="og:title" content={isFR ? 'Prestation canadienne pour les personnes handicapées | deafinfo.ca/fr | ASC-CAD' : 'Canada Disability Benefit | deafinfo.ca | CAD-ASC'} />
        <meta property="og:description" content="CAD-ASC provides resources and support for Canadians navigating disability benefits and tax credits." />
        <meta property="og:image" content={`${SITE_URL}/CAD-ASC-meta-image.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={isFR ? 'Prestation canadienne pour les personnes handicapées | deafinfo.ca/fr | ASC-CAD' : 'Canada Disability Benefit | deafinfo.ca | CAD-ASC'} />
        <meta name="twitter:description" content="CAD-ASC provides resources and support for Canadians navigating disability benefits and tax credits." />
        <meta name="twitter:image" content={`${SITE_URL}/CAD-ASC-meta-image.png`} />
      </Helmet>
    <section className="wrapper">
              {/* OVERVIEW SECTION */}
        <section className="mb-5">
        <TabSection
        videoUrls={["/ASL/DISC ASL CDB and who for.mp4", "/LSQ/PCPH intro and who for.mp4"]} videoLabels={["ASL", "LSQ"]}
        sectionId="sections.form-info"
        activeSectionId={activeSectionId}
        setActiveSectionId={setActiveSectionId}
        setCurrentVideoUrl={setCurrentVideoUrl}
        setVideoSidebarOpen={setVideoSidebarOpen}
        setVideoLabels={setVideoLabels}
        setSelectedVideoIndex={setSelectedVideoIndex}
        setAllVideoUrls={setAllVideoUrls}
        isDefault
        registerSection={registerSection}
        unregisterSection={unregisterSection}>

            <h1 className="form-title">{t('form.page2.form-title')}</h1>
            <div style={{ paddingTop: '0.5rem' }} className="flex row gap-5 section-columns form">
              <div className="col section-copy align-self-center">
                <p>{t('form.page2.form-description')}</p>
                <div className="flex  mt-5">
                  <a href={isFR ? formFr : form} download className="btn-primary btn-download mb-3 me-4">{t('form.page2.button-1')}</a>
                  <button className="btn-secondary btn-download ml-3">{t('form.page2.button-2')}</button>
                </div>
              </div>
              <div className="col section-media">
                <div className="section-who">
                  <h3 className="mb-3">{t('form.page2.who-title')}</h3>
                  <ul>
                  <li>{t('form.page2.who-1')}</li>
                  <li>{t('form.page2.who-2')}</li>
                  <li>{t('form.page2.who-3')}</li>
                  </ul>
                  <p><b>{t('form.page2.source')}</b> <a href="https://www.canada.ca/en/services/benefits/disability/canada-disability-benefit/eligibility.html" target="_blank" rel="noopener noreferrer">{t('form.page2.source-name')}</a></p>
                </div>
              </div>
              </div>
          </TabSection>
        </section>

        <section className="mb-5 form-section">
          <FormViewer
            formImages={formImages}
            formTitle={t('form.page2.form-title')}
            pageOverlays={pageOverlays}
            activeSectionId={activeSectionId}
            setActiveSectionId={setActiveSectionId}
            setCurrentVideoUrl={setCurrentVideoUrl}
            setVideoSidebarOpen={setVideoSidebarOpen}
            setVideoLabels={setVideoLabels}
            setSelectedVideoIndex={setSelectedVideoIndex}
            setAllVideoUrls={setAllVideoUrls}
            registerSection={registerSection}
            unregisterSection={unregisterSection}
          />
        </section>
    </section>
    </>
  );
}

export default Form2;
